import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Container, InputAdornment, Link as MuiLink, MenuItem, Paper, Snackbar, Stack,
    TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {Link, useParams} from 'react-router-dom';
import ApiService from '../network/API';
import GradingAiBar from '../components/grading/GradingAiBar';
import StudentRow, {fmt} from '../components/grading/StudentRow';
import {confirmDialog} from '../confirm';

const AI_TYPES = ['MYSQL_DB', 'GIT_REPO', 'SSH_LAB'];
const TYPE_LABEL = {MYSQL_DB: 'MySQL', GIT_REPO: 'Git', SSH_LAB: 'Лабораторная'};

const stateOf = (s) => (s.published ? 'published' : Object.keys(s.results).length > 0 ? 'draft' : 'todo');

const StatCard = ({label, value, hint, active, onClick, color}) => (
    <Paper variant="outlined" onClick={onClick} sx={{
        p: 1.5, borderRadius: 2, cursor: onClick ? 'pointer' : 'default', flex: '1 1 130px', minWidth: 120,
        borderColor: active ? 'primary.main' : 'divider', bgcolor: active ? 'action.selected' : 'transparent',
        transition: 'border-color .15s',
    }}>
        <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {hint && <Typography variant="caption" color="text.disabled">{hint}</Typography>}
    </Paper>
);

// Проверка работ студентов по критериям — для всех типов задач с рубрикой (лабораторные, MySQL, Git).
// Сверху — сводка и AI-проверка в фоне (прогресс переживает перезагрузку, можно прервать), ниже — компактный список
// студентов с фильтрами; баллы раскрываются по требованию. Студент видит результат и получает баллы в оценку только после публикации.
const AdminCriteriaGradingPage = () => {
    const {problemId} = useParams();
    const [data, setData] = useState(null);
    const [problem, setProblem] = useState(null);
    const [aiStatus, setAiStatus] = useState(null);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [busyIds, setBusyIds] = useState({});
    const [expanded, setExpanded] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [filter, setFilter] = useState('all');
    const [group, setGroup] = useState('all');
    const [search, setSearch] = useState('');
    const [dismissedJob, setDismissedJob] = useState(null);
    const wasRunning = useRef(false);

    const load = useCallback(() => ApiService.getCriteriaGrading(problemId)
        .then(setData)
        .catch((err) => setError(err.message || err.reason || 'Не удалось загрузить данные')), [problemId]);

    const refreshStatus = useCallback(() => ApiService.getCriteriaAiStatus(problemId).then(setAiStatus).catch(() => {}), [problemId]);

    useEffect(() => {
        load();
        refreshStatus();
        ApiService.getProblem(problemId).then(setProblem).catch(() => {});
    }, [load, refreshStatus, problemId]);

    // Пока идёт фоновая проверка — опрашиваем статус; по завершении один раз перезагружаем баллы.
    const job = aiStatus?.job;
    const running = job?.state === 'RUNNING';
    useEffect(() => {
        if (!running) {
            if (wasRunning.current) {
                wasRunning.current = false;
                load();
            }
            return undefined;
        }
        wasRunning.current = true;
        const timer = setInterval(refreshStatus, 2500);
        return () => clearInterval(timer);
    }, [running, refreshStatus, load]);

    // Баллы подтягиваем и по ходу проверки, но не чаще, чем меняется число проверенных.
    const doneCount = job?.done ?? 0;
    useEffect(() => {
        if (running && doneCount > 0) load();
    }, [doneCount, running, load]);

    const students = useMemo(() => data?.students || [], [data]);
    const aiType = AI_TYPES.includes(data?.problemType);
    const scripts = data?.problemType === 'SSH_LAB'; // лабораторные проверяются скриптами критериев, без AI
    const aiEnabled = !!aiStatus?.config?.enabled;
    const canAi = aiType && aiEnabled && !running;

    const lastRuns = useMemo(() => {
        const map = new Map();
        (aiStatus?.lastRuns || []).forEach((r) => map.set(r.userId, r));
        return map;
    }, [aiStatus]);

    const nameOf = useCallback((id) => students.find((s) => s.userId === id)?.fullName || `№${id}`, [students]);

    const aiStateOf = useCallback((id) => {
        if (!running) return null;
        if (job.currentUserId === id) return 'running';
        return job.queued?.includes(id) ? 'queued' : null;
    }, [running, job]);

    const stats = useMemo(() => {
        const counts = {all: students.length, published: 0, draft: 0, todo: 0};
        let sum = 0, graded = 0;
        const max = data?.totalMaxPoints || 0;
        students.forEach((s) => {
            counts[stateOf(s)] += 1;
            if (Object.keys(s.results).length) {
                sum += Object.values(s.results).reduce((a, r) => a + r.earnedPoints, 0);
                graded += 1;
            }
        });
        return {...counts, average: graded && max ? sum / graded : null};
    }, [students, data]);

    const groups = useMemo(() => {
        const list = [];
        students.forEach((s) => {
            if (s.groupId != null && !list.some((g) => g.id === s.groupId)) list.push({id: s.groupId, name: s.groupName});
        });
        return list;
    }, [students]);
    const selectedGroup = groups.find((g) => String(g.id) === String(group)) || null;

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return students.filter((s) => (
            (filter === 'all' || stateOf(s) === filter)
            && (!selectedGroup || s.groupId === selectedGroup.id)
            && (!q || s.fullName.toLowerCase().includes(q))
        ));
    }, [students, filter, selectedGroup, search]);

    const notify = useCallback((severity, message) => setToast({severity, message}), []);
    const setBusy = (id, value) => setBusyIds((prev) => ({...prev, [id]: value}));

    const save = useCallback(async (student, results, published) => {
        setBusy(student.userId, true);
        try {
            await ApiService.saveCriteriaResults(problemId, student.userId, {results, published});
            notify('success', published === true ? 'Опубликовано' : published === false ? 'Публикация снята' : 'Сохранено');
            await load();
        } catch (err) {
            notify('error', err.message || err.reason || 'Не удалось сохранить');
        } finally {
            setBusy(student.userId, false);
        }
    }, [problemId, load, notify]);

    const publish = useCallback((student, value) => save(student, [], value), [save]);

    // Точечная проверка — тот же фоновый процесс, что и массовая: страница не блокируется.
    const startAi = useCallback(async (ids, overwriteManual) => {
        try {
            await ApiService.runCriteriaAi(problemId, true, null, true, ids, overwriteManual);
            setDismissedJob(null);
            await refreshStatus();
        } catch (err) {
            notify(err.status === 409 ? 'warning' : 'error', err.reason || err.message || 'Не удалось запустить проверку');
        }
    }, [problemId, refreshStatus, notify]);

    const checkOne = useCallback(async (student) => {
        const hasManual = Object.values(student.results || {}).some((r) => r.source === 'TEACHER');
        if (hasManual && !await confirmDialog(`У студента есть баллы, которые вы выставили вручную. Перепроверить ${scripts ? 'автопроверкой' : 'AI'} и перезаписать их?`)) return;
        if (student.autoPublished && !await confirmDialog(`Результат опубликован автоматически. Перепроверить ${scripts ? 'автопроверкой' : 'AI'} и заменить его? Студент сразу увидит новые баллы.`)) return;
        startAi([student.userId], hasManual);
    }, [startAi, scripts]);

    const checkSelected = async () => {
        const ids = [...selected];
        if (!await confirmDialog(`Проверить ${scripts ? 'автопроверкой' : 'через AI'} выбранных студентов (${ids.length})? Это фоновый процесс. Опубликованное вами и ваши ручные баллы не изменятся.`)) return;
        await startAi(ids, false);
        setSelected(new Set());
    };

    const publishSelected = async () => {
        const targets = students.filter((s) => selected.has(s.userId) && !s.published && Object.keys(s.results).length > 0);
        if (!targets.length) {
            notify('info', 'Среди выбранных нет черновиков для публикации');
            return;
        }
        if (!await confirmDialog(`Опубликовать результаты выбранных студентов (${targets.length})? Они сразу увидят баллы, а оценка лекции пересчитается.`)) return;
        for (const s of targets) {
            try {
                await ApiService.saveCriteriaResults(problemId, s.userId, {results: [], published: true});
            } catch (err) {
                notify('error', `${s.fullName}: ${err.message || err.reason || 'ошибка'}`);
            }
        }
        setSelected(new Set());
        notify('success', `Опубликовано: ${targets.length}`);
        load();
    };

    const publishAllDrafts = async () => {
        const targets = visible.filter((s) => stateOf(s) === 'draft');
        if (!await confirmDialog(`Опубликовать все черновики в списке (${targets.length})? Студенты сразу увидят баллы, а оценка лекции пересчитается.`)) return;
        setSelected(new Set(targets.map((s) => s.userId)));
        for (const s of targets) {
            try {
                await ApiService.saveCriteriaResults(problemId, s.userId, {results: [], published: true});
            } catch (err) {
                notify('error', `${s.fullName}: ${err.message || err.reason || 'ошибка'}`);
            }
        }
        setSelected(new Set());
        notify('success', `Опубликовано: ${targets.length}`);
        load();
    };

    const toggleSelect = useCallback((id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }), []);
    const toggleExpand = useCallback((id) => setExpanded((prev) => (prev === id ? null : id)), []);

    const allVisibleSelected = visible.length > 0 && visible.every((s) => selected.has(s.userId));
    const toggleAllVisible = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((s) => s.userId)));

    if (error) return <Container sx={{py: 4}}><Alert severity="error">{error}</Alert></Container>;
    if (!data) return <Box display="flex" justifyContent="center" py={10}><CircularProgress/></Box>;

    const draftsInView = visible.filter((s) => stateOf(s) === 'draft').length;
    const jobDismissed = job && dismissedJob === job.startedAt;

    return (
        <Container maxWidth="lg" sx={{py: 3}}>
            <Breadcrumbs sx={{mb: 1.5}}>
                <MuiLink component={Link} to={`/problems/${problemId}`} underline="hover" color="inherit">{problem?.title || 'Задача'}</MuiLink>
                <Typography color="text.primary">Проверка работ</Typography>
            </Breadcrumbs>

            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{mb: 0.5}}>
                <Typography variant="h5" fontWeight={800}>Проверка работ</Typography>
                {TYPE_LABEL[data.problemType] && <Chip size="small" label={TYPE_LABEL[data.problemType]}/>}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {problem?.title ? `«${problem.title}». ` : ''}Максимум {fmt(data.totalMaxPoints)} б. Студент видит баллы и они идут в оценку лекции только после публикации;
                напарнику баллы засчитываются автоматически.
            </Typography>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                <StatCard label="Студентов" value={stats.all} active={filter === 'all'} onClick={() => setFilter('all')}/>
                <StatCard label="Не проверено" value={stats.todo} color={stats.todo ? 'warning.main' : undefined} active={filter === 'todo'} onClick={() => setFilter('todo')}/>
                <StatCard label="Черновики" value={stats.draft} hint="ждут публикации" active={filter === 'draft'} onClick={() => setFilter('draft')}/>
                <StatCard label="Опубликовано" value={stats.published} color="success.main" active={filter === 'published'} onClick={() => setFilter('published')}/>
                <StatCard label="Средний балл" value={stats.average == null ? '—' : fmt(stats.average)} hint={`из ${fmt(data.totalMaxPoints)}`}/>
            </Stack>

            {aiType && (
                <GradingAiBar scripts={scripts} problemId={problemId} status={aiStatus} onStatus={setAiStatus} onStarted={() => setDismissedJob(null)}
                              groupId={selectedGroup ? selectedGroup.id : null} groupName={selectedGroup ? selectedGroup.name : null}
                              nameOf={nameOf} jobDismissed={jobDismissed} onDismissJob={() => setDismissedJob(job?.startedAt)}/>
            )}

            {data.criteria.length === 0 && (
                <Alert severity="warning" sx={{mb: 2}}>У задачи нет критериев — добавьте их на странице «Критерии оценки».</Alert>
            )}

            <Paper variant="outlined" sx={{borderRadius: 2, overflow: 'hidden'}}>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} alignItems={{sm: 'center'}} sx={{p: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                    <TextField size="small" placeholder="Поиск по фамилии" value={search} onChange={(e) => setSearch(e.target.value)} sx={{minWidth: 220}}
                               slotProps={{input: {startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}}/>
                    {groups.length > 1 && (
                        <TextField select size="small" value={group} onChange={(e) => setGroup(e.target.value)} sx={{minWidth: 170}}>
                            <MenuItem value="all">Все группы ({students.length})</MenuItem>
                            {groups.map((g) => (
                                <MenuItem key={g.id} value={String(g.id)}>{g.name} ({students.filter((s) => s.groupId === g.id).length})</MenuItem>
                            ))}
                        </TextField>
                    )}
                    <Box sx={{flexGrow: 1}}/>
                    {draftsInView > 0 && (
                        <Button size="small" variant="outlined" color="success" onClick={publishAllDrafts}>Опубликовать все черновики ({draftsInView})</Button>
                    )}
                </Stack>

                {selected.size > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap
                           sx={{px: 2, py: 1, bgcolor: 'action.selected', borderBottom: 1, borderColor: 'divider'}}>
                        <Typography variant="body2" fontWeight={600}>Выбрано: {selected.size}</Typography>
                        {aiType && <Button size="small" variant="contained" color="secondary" disabled={!canAi} onClick={checkSelected}>{scripts ? 'Проверить' : 'Проверить AI'}</Button>}
                        <Button size="small" variant="contained" color="success" onClick={publishSelected}>Опубликовать</Button>
                        <Button size="small" onClick={() => setSelected(new Set())}>Снять выбор</Button>
                    </Stack>
                )}

                {visible.length > 0 && (
                    <Stack direction="row" alignItems="center" sx={{px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover'}}>
                        <Button size="small" onClick={toggleAllVisible}>{allVisibleSelected ? 'Снять выбор' : `Выбрать всех (${visible.length})`}</Button>
                    </Stack>
                )}

                {visible.length === 0 ? (
                    <Typography color="text.secondary" sx={{p: 3}}>
                        {students.length === 0
                            ? 'Пока нет студентов: здесь появятся те, кто начал работу над задачей.'
                            : 'Никого не найдено — измените фильтр или поиск.'}
                    </Typography>
                ) : data.criteria.length > 0 && visible.map((s) => (
                    <StudentRow
                        key={s.userId}
                        student={s}
                        criteria={data.criteria}
                        totalMax={data.totalMaxPoints}
                        run={lastRuns.get(s.userId)}
                        aiState={aiStateOf(s.userId)}
                        selected={selected.has(s.userId)}
                        onSelect={toggleSelect}
                        expanded={expanded === s.userId}
                        onToggle={toggleExpand}
                        onSave={save}
                        onPublish={publish}
                        onAi={checkOne}
                        aiType={aiType}
                        scripts={scripts}
                        canAi={canAi}
                        busy={!!busyIds[s.userId]}
                    />
                ))}
            </Paper>

            <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
                {toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}
            </Snackbar>
        </Container>
    );
};

export default AdminCriteriaGradingPage;
